/**
 * Shared nav tabs and breadcrumbs styling for consistency across the app.
 * Unified Page Header System: 4-row structure used across Shift, Procedures, Archive, Competence.
 */
import { type SxProps, type Theme, alpha } from "@mui/material";

/** Unified Page Header tokens — consistent across Archive, Competence, Procedures, Shift */
export const unifiedHeaderTokens = {
	/** Header block padding */
	paddingX: { xs: 2, sm: 2 },
	paddingY: { xs: 2, sm: 2 },
	rowGap: 0,
	/** Input height (px) */
	inputHeight: 32,
	/** Button height (px) */
	buttonHeight: 32,
	/** Page title — slightly larger */
	titleFontSize: { xs: "1.0625rem", sm: "1.25rem" },
	titleFontWeight: 700,
	/** Search input & header text */
	searchInputFontSize: "0.875rem",
	/** Secondary buttons (Filters, Actions) */
	buttonFontSize: "0.875rem",
	buttonFontWeight: 600,
	buttonBorderRadius: 2,
	buttonPaddingX: 1.5,
	breadcrumbFontSize: "0.75rem",
	tabsFontSize: "0.9375rem",
	/** Filter panel labels */
	filterLabelFontSize: "0.875rem",
};

/** Filter panel: date input width (px) */
export const filterPanelDateWidth = 140;

/** Filter panel: datetime-local input width (px) — slightly wider for time picker */
export const filterPanelDatetimeWidth = 160;

/** Filter panel: short select width (e.g. Updated) */
export const filterPanelSelectShortWidth = 100;

/** Filter panel: standard select width */
export const filterPanelSelectWidth = 160;

/** Unified filter panel style — inputs, buttons, reset all consistent across Archive, Competence, Procedures, Shift */
export const sharedFilterPanelSx = (theme: Theme): SxProps<Theme> => ({
	display: "flex",
	flexDirection: { xs: "column", sm: "row" },
	alignItems: { xs: "stretch", sm: "center" },
	flexWrap: "wrap",
	gap: 1,
	p: 1,
	px: 1.5,
	bgcolor:
		theme.palette.mode === "dark"
			? "rgba(255,255,255,0.04)"
			: "rgba(0,0,0,0.02)",
	borderRadius: 2,
	border: `1px solid ${theme.palette.divider}`,
	// All inputs: same height, radius
	"& .MuiTextField-root .MuiOutlinedInput-root": {
		height: unifiedHeaderTokens.inputHeight,
		minHeight: unifiedHeaderTokens.inputHeight,
		borderRadius: 2,
		"& .MuiInputBase-input": { py: { xs: 0.5, sm: 0.25 } },
	},
	"& .MuiFormControl-root .MuiOutlinedInput-root": {
		height: unifiedHeaderTokens.inputHeight,
		minHeight: unifiedHeaderTokens.inputHeight,
		borderRadius: 2,
		"& .MuiSelect-select": { py: { xs: 0.5, sm: 0.25 } },
	},
	"& .MuiAutocomplete-root .MuiOutlinedInput-root": {
		height: unifiedHeaderTokens.inputHeight,
		minHeight: unifiedHeaderTokens.inputHeight,
		borderRadius: 2,
	},
	// Reset/Clear button: same everywhere
	"& .MuiButton-root.MuiButton-text": {
		textTransform: "none",
		fontWeight: unifiedHeaderTokens.buttonFontWeight,
		minHeight: unifiedHeaderTokens.buttonHeight,
		fontSize: unifiedHeaderTokens.buttonFontSize,
		"& .MuiSvgIcon-root": { fontSize: 18 },
	},
	// Filter labels (Date range, Updated, etc.) and checkbox labels
	"& .MuiTypography-root": {
		fontSize: unifiedHeaderTokens.filterLabelFontSize,
	},
	"& .MuiFormControlLabel-label": {
		fontSize: unifiedHeaderTokens.filterLabelFontSize,
	},
});

/** Sx for Reset/Clear button inside filter panel — use startIcon={<RestartAltIcon sx={{ fontSize: 18 }} />} */
export const sharedFilterResetButtonSx: SxProps<Theme> = {
	textTransform: "none",
	fontWeight: 600,
	minHeight: unifiedHeaderTokens.buttonHeight,
	alignSelf: { xs: "flex-start", sm: "center" },
	ml: { sm: "auto" },
};

/** Unified Tabs sx – procedures, shift, archive, competence. Larger text. */
export const sharedTabsSx: SxProps<Theme> = {
	width: "100%",
	minWidth: 0,
	minHeight: 0,
	borderBottom: 1,
	borderColor: "divider",
	"& .MuiTabs-flexContainer": {
		minHeight: { xs: 40, sm: 38 },
	},
	"& .MuiTab-root": {
		textTransform: "none",
		fontSize: unifiedHeaderTokens.tabsFontSize,
		minHeight: { xs: 40, sm: 38 },
		py: { xs: 0.75, sm: 0.5 },
		px: { xs: 1.25, sm: 1.5 },
		minWidth: { xs: "auto", sm: 80 },
		color: "text.secondary",
		fontWeight: 500,
		"& span": {
			color: "inherit",
			fontWeight: "inherit",
		},
		"&.Mui-selected": {
			color: "text.primary",
			fontWeight: 700,
			"& span": {
				color: "inherit",
				fontWeight: "inherit",
			},
		},
	},
	"& .MuiTabs-indicator": {
		height: 2,
	},
	"& .MuiTabs-scrollButtons": {
		width: 32,
	},
	"& .MuiTabs-scrollButtons.Mui-disabled": {
		opacity: 0.3,
	},
};

export const mobilePillTabsSx = (theme: Theme): SxProps<Theme> => ({
	width: "100%",
	minWidth: 0,
	minHeight: 0,
	py: 0.25,
	"& .MuiTabs-indicator": { display: "none" },
	"& .MuiTabs-flexContainer": {
		gap: 0.75,
	},
	"& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.2 },
	"& .MuiTab-root": {
		minHeight: 40,
		px: 1.5,
		py: 1,
		minWidth: "fit-content",
		borderRadius: 999,
		textTransform: "none",
		fontSize: "0.8125rem",
		fontWeight: 700,
		color: "text.secondary",
		border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
		"& span": {
			color: "inherit",
			fontWeight: "inherit",
		},
	},
	"& .MuiTab-root.Mui-selected": {
		color: "text.primary",
		borderColor: alpha(theme.palette.primary.main, 0.35),
		"& span": {
			color: "inherit",
			fontWeight: "inherit",
		},
	},
});

/** Unified secondary/outline button sx (Filters, Actions, etc.) — same across all headers */
export const sharedSecondaryButtonSx: SxProps<Theme> = {
	height: unifiedHeaderTokens.buttonHeight,
	minHeight: unifiedHeaderTokens.buttonHeight,
	borderRadius: unifiedHeaderTokens.buttonBorderRadius,
	textTransform: "none" as const,
	fontWeight: unifiedHeaderTokens.buttonFontWeight,
	fontSize: unifiedHeaderTokens.buttonFontSize,
	px: unifiedHeaderTokens.buttonPaddingX,
};

/** Unified secondary Tabs sx – for nested tabs (e.g. procedure updates, lab edit subtabs) */
export const sharedSecondaryTabsSx: SxProps<Theme> = {
	...sharedTabsSx,
	mt: 1,
};

/** Unified breadcrumbs wrapper sx */
export const sharedBreadcrumbsSx: SxProps<Theme> = {
	py: 0.25,
	px: { xs: 1, sm: 1.25 },
	"& .MuiBreadcrumbs-separator": {
		fontSize: 12,
		color: "text.disabled",
	},
	"& .MuiBreadcrumbs-li": {
		minWidth: 0,
		fontSize: "0.75rem", // 12px
	},
	"& .MuiBreadcrumbs-ol": {
		flexWrap: "wrap",
		gap: 0.25,
	},
};
